// „Spiel wie …“, Ansicht: Brett aus Sicht des gewählten Spielers, Punktestand, Urteil über den
// letzten Rateversuch, Zugliste mit Punkten. Ab jeder Stellung lässt sich gegen einen Bot weiterspielen.
import { h, type VNode } from 'snabbdom';
import { board } from '../board';
import type { GuessEntry, GuessGame } from '../guessgame';
import { getLang, t } from '../i18n';
import { materialLead } from '../material';
import type { PlayCtrl } from '../playctrl';
import { continueFrom, shortName } from '../playstart';
import { playerBar } from './playerbar';

const btn = (label: string, on: () => void) => h('button.btn', { on: { click: on } }, t(label));

function verdict(e: GuessEntry, master: string): VNode[] {
  const loss = e.loss ?? 0;
  const why = {
    same: () => t('Genau der Partiezug.'),
    mate: () => t('Matt – besser geht es nicht.'),
    missed: () => t('Der Partiezug setzt matt.'),
    engine: () => (loss <= 0 ? t('Laut Stockfish mindestens so gut wie der Partiezug.')
      : t('Laut Stockfish {x} % weniger Gewinnchance als der Partiezug.', { x: loss.toLocaleString(getLang(), { maximumFractionDigits: 1 }) })),
  }[e.kind]();
  return [
    h('p', [h('b', e.guessSan), ` · ${master}: `, h('b', e.masterSan), ' ', h(`span.pts${e.points >= 8 ? '.ok' : e.points >= 4 ? '.mid' : '.low'}`, t('{n} Punkte', { n: e.points }))]),
    h('p.muted.small', why),
  ];
}

function panel(c: PlayCtrl, g: GuessGame): VNode {
  const me = shortName(g.side === 'white' ? g.meta.white : g.meta.black);
  const last = g.entries[g.entries.length - 1];
  const status = g.comparing ? [h('p.muted.small', t('Stockfish vergleicht …'))]
    : g.over ? [h('p', t('Partie zu Ende: {a} von {b} Punkten, {n} Züge genau getroffen.', { a: g.score, b: g.max, n: g.exact }))]
      : last ? verdict(last, me) : [h('p.muted.small', t('Ziehe für {n}. Stockfish vergleicht deinen Zug mit dem Partiezug.', { n: me }))];
  return h('div.setup.coach.guess', [
    h('div.hd', h('div.row', [h('h3', t('Spiel wie {n}', { n: me })), h('span.score', `${g.score} / ${g.max}`)])),
    h('div.fld', status),
    g.error ? h('div.fld', h('p.error', g.error)) : null,
    h('div.fld', h('p.muted.small', t('10 Punkte: Partiezug oder laut Stockfish gleich gut. 8, 6, 4, 2: weniger als 2, 5, 10, 20 % Gewinnchance verloren. Sonst 0.'))),
    h('div.fld', h('div.row.btns', [btn('Ab hier weiterspielen', () => continueFrom(c, g.fen)), btn('Beenden', () => ((c.guess = null), c.redraw()))])),
  ]);
}

function moveList(g: GuessGame): VNode {
  const pts = new Map(g.entries.map(e => [e.ply, e.points]));
  const cells: VNode[] = [];
  g.plies.slice(1, g.i + 1).forEach((p, k) => {
    const [, turn, , , , full] = g.plies[k].fen.split(' ');
    if (turn === 'w') cells.push(h('span.index', full));
    else if (k === 0) cells.push(h('span.index', full), h('span.move', '…'));
    cells.push(h('span.move', [p.san ?? '', pts.has(k + 1) ? h('small.pts', ` ${pts.get(k + 1)}`) : null]));
  });
  return h('div.tview.play-moves', { hook: { postpatch: (_, v) => ((v.elm as HTMLElement).scrollTop = 1e6) } }, cells);
}

export function guessView(c: PlayCtrl, g: GuessGame): VNode {
  const name = (color: 'white' | 'black') => (color === 'white' ? g.meta.white : g.meta.black);
  const other = g.side === 'white' ? 'black' : 'white';
  const bar = (where: 'top' | 'bottom', color: 'white' | 'black') =>
    playerBar(where, { color, name: name(color), active: !g.over && g.turn === color, lead: materialLead(g.fen) * (color === 'white' ? 1 : -1) });
  const san = g.plies[g.i].san ?? '';
  return h('section.play', [
    h('div.play-body', [
      h('div.board-col', [
        bar('top', other),
        board('play-board', {
          fen: g.fen,
          orientation: g.side,
          turnColor: g.turn,
          check: /[+#]$/.test(san) ? g.turn : false,
          lastMove: g.last,
          coordinates: true,
          animation: { enabled: true, duration: 250 },
          movable: { free: false, color: g.side, dests: g.dests(), showDests: true, events: { after: (o, d) => void g.move(o, d) } },
          premovable: { enabled: false },
          drawable: { enabled: true, visible: true, autoShapes: [] },
        }),
        bar('bottom', g.side),
      ]),
      h('div.side', [
        panel(c, g),
        h('div.setup', [h('div.hd', h('h3', `${g.meta.white} – ${g.meta.black}`)), moveList(g)]),
      ]),
    ]),
  ]);
}
